import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { normalizeNoteContent } from './RichNoteEditor';

// DOMPurify config — allow the elements + attrs that the rich editor can
// generate (including iframe for embeds and our highlight / table classes).
// Iframes are restricted to known providers via the buildEmbedHtml helper, but
// we add a defence-in-depth allowlist on src here too.
const ALLOWED_IFRAME_HOSTS = [
  'www.youtube.com', 'youtube.com', 'player.vimeo.com',
  'www.loom.com', 'loom.com',
  'docs.google.com', 'drive.google.com',
  'www.figma.com', 'figma.com',
];

const sanitize = (html) =>
  DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'loading', 'referrerpolicy', 'target', 'rel'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/):)/i,
    FORBID_TAGS: ['script', 'style'],
  });

// Strip iframes whose src host isn't in our allowlist — runs after DOMPurify.
const enforceIframeAllowlist = (html) => {
  if (typeof document === 'undefined' || !html) return html;
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html;
  wrapper.querySelectorAll('iframe').forEach((frame) => {
    try {
      const u = new URL(frame.src);
      const host = u.hostname.replace(/^www\./, '');
      if (!ALLOWED_IFRAME_HOSTS.some((h) => h.replace(/^www\./, '') === host)) {
        frame.remove();
      }
    } catch (_) {
      frame.remove();
    }
  });
  return wrapper.innerHTML;
};

const NoteContentRenderer = ({ content, className = '' }) => {
  const html = useMemo(() => {
    const normalised = normalizeNoteContent(content);
    if (!normalised) return '';
    return enforceIframeAllowlist(sanitize(normalised));
  }, [content]);

  if (!html) return null;
  return (
    <div
      className={`note-rich-content prose prose-sm max-w-none ${className}`}
      data-testid="note-rich-content"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default NoteContentRenderer;
